import posthog from 'posthog-js';
import { Userpilot } from 'userpilot';

/**
 * A curated subset of PostHog events we also forward to Userpilot (for tour targeting/analytics).
 * Deliberately NOT the full PH event list — keyed by PH event value → Userpilot event name.
 * Multiple PH events can map to the same Userpilot event (e.g. course start/resume/relaunch).
 */
const USERPILOT_EVENTS: Record<string, string> = {
  'catalogue:viewed': 'course_catalogue_viewed',
  'passport:viewed': 'skills_passport_viewed',
  'benchmark:viewed': 'run_benchmark',
  'benchmark:unlocked_free_job': 'free_job_unlocked',
  'solutions:opened': 'viewed_solutions',
  'manage_training:enrol_viewed': 'enrol_a_student',
  'manage_training:enrol_create_student_clicked': 'invite_a_student',
  'manage_training:students_courses_viewed': 'view_student_enrolment_history',
  'mycourses:click_start': 'course_launched',
  'mycourses:click_resume': 'course_launched',
  'mycourses:click_relaunch': 'course_launched',
};

/**
 * Capture a product event. Event names follow the `category:action` convention and are forced to
 * lowercase (PostHog actions are case-sensitive). No-ops safely when capturing is opted out
 * (e.g. preview mode) or PostHog isn't initialised. A curated subset is also forwarded to Userpilot.
 */
export const track = (event: string, props?: Record<string, unknown>) => {
  // Telemetry must never break the caller — several track() sites sit on user-visible paths where
  // a throw would strand the UI mid-action. Same rule as the Userpilot.identify guard in App.tsx.
  try {
    posthog.capture(event.toLowerCase(), props);

    const userpilotEvent = USERPILOT_EVENTS[event];
    if (userpilotEvent) Userpilot.track(userpilotEvent);
  } catch (e) {
    console.error('Analytics capture failed', e);
  }
};

/**
 * Whether PostHog can actually record anything in THIS document.
 *
 * False in the preview sandbox, where `init` is skipped outright — but also, less obviously, in a
 * session that BOOTED with preview intent and ended up un-armed: a "Show my team" bootstrap that
 * fails clears the intent and renders the perfectly normal app (`bootstrapPreview`), yet the
 * kill-switch in `index.tsx` keyed off that intent at boot and there is no way to revive PostHog
 * for the rest of the document. `isPreviewArmed()` is false there, so "not armed" does NOT imply
 * "PostHog is live" and must never be used as a proxy for it.
 *
 * Anything that CONSUMES a queued signal has to check this first, or it destroys the queue in a
 * document that cannot report it. `capture()` itself is already a safe no-op when this is false.
 */
export const isPostHogLive = (): boolean => {
  try {
    return posthog.__loaded === true && !posthog.has_opted_out_capturing();
  } catch {
    return false;
  }
};

/**
 * Drop the analytics identity so the next person identified in this browser starts clean —
 * otherwise the stored distinct_id carries over and their events merge into the previous person's
 * profile. Callers keep their own identity caches (App.userpilotIdentifiedId).
 *
 * NOT covered: Hotjar (klayowebshared Analytics.identify) holds a third identity and react-hotjar
 * exposes no reset.
 */
export const resetAnalyticsIdentity = () => {
  posthog.reset();
  Userpilot.reset();
};

/**
 * Tell Userpilot the client-side page changed so it re-scans the DOM and records the page view.
 * The app is a SPA, so Userpilot never sees navigations on its own — every route change must call
 * this. The explicit `url` is passed so the view is attributed to the destination rather than a
 * stale URL. Fires in the preview sandbox too (Userpilot is now loaded there for demo-page tours);
 * the url is a synthetic route, so no PII is sent.
 */
export const trackPageView = (url: string) => {
  Userpilot.reload(url);
};
